import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { toLower } from 'lodash';
import { Subject, takeUntil } from 'rxjs';
import { ScreenType } from 'src/app/enums/ScreenType';
import { IRiskQuestions } from 'src/app/models/riskProfile';
import { AumyeResponsiveScreenService } from 'src/app/services/screenResize/aumye-responsive-screen.service';

@Component({
  selector: 'app-analytics-information-dialog',
  templateUrl: './analytics-information-dialog.component.html',
  styleUrl: './analytics-information-dialog.component.scss',
})
export class AnalyticsInformationDialogComponent implements OnInit, OnDestroy {
  @Output() saveQuestions = new EventEmitter();
  @Input() set analyticsData(val: IRiskQuestions[]) {
    if (val.length > 0) {
      this.riskAnalyticsData = val;
      this.updateRiskQuestionsForm(val);
    }
  }
  riskAnalyticsData: IRiskQuestions[] = [];
  @Input() isDataLoaded = false;
  // Current expansion panel step
  currentStep = 0;
  // Form groups array for all risk questions
  riskQuestionsForms: FormGroup[] = [];
  // Flag to check if all questions are answered
  isAllAnswered = false;
  // Current screen size
  screenSize: ScreenType;
  // Screen type enum for template usage
  ScreenType = ScreenType;

  // Edit mode flag - NEW
  isEditMode = false;
  // Flag to track if form has existing data - NEW
  hasExistingData = false;

  // Risk level options
  riskLevelOptions = [
    { label: 'High', value: 100 },
    { label: 'Medium', value: 50 },
    { label: 'Low', value: 25 },
    { label: 'No Risk', value: 0 },
  ];
  // Subject for unsubscribing observables
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private screenService: AumyeResponsiveScreenService
  ) {}

  ngOnInit(): void {
    // Subscribe to screen size changes
    this.screenService
      .getScreenType()
      .pipe(takeUntil(this.destroy$))
      .subscribe((screenType) => {
        this.screenSize = screenType;
      });

    // Initialize form groups for each risk question
    this.initFormGroups();

    this.checkAllAnswered();

    // Listen for form value changes to update calculations and validation state
    this.listenToFormChanges();
  }

  ngOnDestroy(): void {
    // Clean up subscriptions when component is destroyed
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateRiskQuestionsForm(analyticsData: IRiskQuestions[]): void {
    this.hasExistingData = analyticsData.length > 0;
    analyticsData.forEach((data) => {
      if (parseInt(data.riskTotal) == 0) {
      }
      // Find the matching form group by comparing question text
      const matchingFormIndex = this.riskQuestionsForms.findIndex(
        (formGroup) => {
          const formTitle = formGroup.get('title')?.value;
          return formTitle.toLowerCase() === data.riskQuestion.toLowerCase();
        }
      );
      // If we found a matching form group, update it
      if (matchingFormIndex !== -1) {
        const formGroup = this.riskQuestionsForms[matchingFormIndex];

        // Update form controls with the analytics data
        formGroup.patchValue({
          criticality: !isNaN(parseInt(data.riskCriticality))
            ? parseInt(data.riskCriticality)
            : 50,
          frequency: !isNaN(parseInt(data.riskFrequency))
            ? parseInt(data.riskFrequency)
            : 10,
          totalScore: !isNaN(parseInt(data.riskTotal))
            ? parseInt(data.riskTotal)
            : 500,
        });
        // NEW: Disable form controls if not in edit mode and has existing data
        setTimeout(() => {
          if (this.hasExistingData && !this.isEditMode) {
            this.disableFormGroup(formGroup);
          }
        }, 0);

        if (parseInt(data.riskTotal) == 0) {
        }
      }
    });
  }
  /**
   * Initialize form groups for all risk questions
   */
  // private initFormGroups(): void {
  //   RISK_QUESTIONS.forEach((question) => {
  //     const formGroup = this.fb.group({
  //       title: [question.title, Validators.required],
  //       criticality: [50, Validators.required], // Default to Medium
  //       frequency: [
  //         10,
  //         [
  //           Validators.required,
  //           Validators.min(0),
  //           Validators.pattern(/^[0-9]+$/),
  //         ],
  //       ], // Only allow positive numbers
  //       totalScore: [{ value: 500, disabled: true }], // Default calculation: 50 * 10 = 500
  //     });

  //     this.riskQuestionsForms.push(formGroup);
  //   });
  // }
  private initFormGroups(): void {
    RISK_QUESTIONS.forEach((question) => {
      const formGroup = this.fb.group({
        title: [question.title, Validators.required],
        criticality: [null, Validators.required],
        frequency: [
          0,
          [
            Validators.required,
            Validators.min(0),
            Validators.pattern(/^[0-9]+$/),
          ],
        ],
        totalScore: [{ value: 0, disabled: true }],
      });

      this.riskQuestionsForms.push(formGroup);
    });
  }
  // NEW: Method to enable edit mode

  enableEditMode(): void {
    this.isEditMode = true;
    this.riskQuestionsForms.forEach((formGroup) => {
      this.enableFormGroup(formGroup);
    });
  }

  // NEW: Method to cancel edit mode

  cancelEdit(): void {
    this.isEditMode = false;
    if (this.hasExistingData) {
      // Temporarily disable listeners during restore
      this.destroy$.next();
      this.updateRiskQuestionsForm(this.riskAnalyticsData);
      this.listenToFormChanges(); // Re-enable listeners
    }
    this.riskQuestionsForms.forEach((formGroup) => {
      this.disableFormGroup(formGroup);
    });
  }

  // NEW: Helper method to disable form group (except totalScore which is always disabled)

  private disableFormGroup(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      if (key !== 'totalScore') {
        // totalScore should always remain disabled

        formGroup.get(key)?.disable();
      }
    });
  }

  // NEW: Helper method to enable form group (except totalScore which is always disabled)

  private enableFormGroup(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      if (key !== 'totalScore') {
        // totalScore should always remain disabled

        formGroup.get(key)?.enable();
      }
    });
  }

  // NEW: Check if form controls are disabled (for template usage)

  isFormDisabled(): boolean {
    return this.hasExistingData && !this.isEditMode;
  }

  /**
   * Listen to form value changes to update calculations and validation
   */
  private listenToFormChanges(): void {
    this.riskQuestionsForms.forEach((form) => {
      form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.updateTotalScore(form);
        this.checkAllAnswered();
      });
    });
  }

  /**
   * Update total score calculation based on criticality and frequency
   */
  private updateTotalScore(form: FormGroup): void {
    const criticality = form.get('criticality')?.value || 0;
    const frequency = form.get('frequency')?.value || 0;
    const total = criticality * frequency;

    // Prevent circular calls
    const currentTotal = form.get('totalScore')?.value;
    if (currentTotal !== total) {
      form.get('totalScore')?.setValue(total, { emitEvent: false });
    }
  }

  /**
   * Set the current expansion panel step
   */
  setStep(index: number): void {
    this.currentStep = index;
  }

  /**
   * Go to the next question
   */
  nextStep(): void {
    if (this.currentStep < this.riskQuestionsForms.length - 1) {
      this.currentStep++;
      this.checkAllAnswered();
    }
  }

  /**
   * Go to the previous question
   */
  prevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  /**
   * Check if the form at the specified index is invalid
   */
  isFormInvalid(index: number): boolean {
    return this.riskQuestionsForms[index]?.invalid || false;
  }

  /**
   * Check if all questions have been validly answered
   */
  checkAllAnswered(): void {
    const invalidForms = this.riskQuestionsForms.filter((form) => form.invalid);
    this.isAllAnswered = invalidForms.length === 0;
  }

  /**
   * Get the total risk score from all questions
   */
  getTotalRiskScore(): number {
    return this.riskQuestionsForms.reduce((total, form) => {
      const score = form.get('totalScore')?.value || 0;
      return total + score;
    }, 0);
  }

  /**
   * Save answers and close dialog
   */
  saveAnswers(): void {
    // Transform form data back to RiskQuestions format
    const answeredQuestions = this.riskQuestionsForms.map((form, index) => {
      const originalQuestion = RISK_QUESTIONS[index];
      const criticality = form.get('criticality')?.value || 0;
      const frequency = form.get('frequency')?.value || 0;

      return {
        ...originalQuestion,
        criticality: criticality,
        numberOfTime: frequency,
        totalScore: criticality * frequency,
        isAnswered: true,
      };
    });
    // NEW: After saving, disable edit mode if there was existing data

    if (this.hasExistingData) {
      this.isEditMode = false;

      this.riskQuestionsForms.forEach((formGroup) => {
        this.disableFormGroup(formGroup);
      });
    }

    this.saveQuestions.emit(answeredQuestions);
  }
  getAnsweredCount(): number {
    return this.riskQuestionsForms.filter((form) => form.valid)?.length;
  }
  createRiskQuestion(title: string): RiskQuestions {
    return {
      title,
      options: [
        { label: 'High', selected: 0, value: 100 },
        { label: 'Medium', selected: 0, value: 50 },
        { label: 'Low', selected: 0, value: 25 },
        { label: 'No Risk', selected: 0, value: 0 },
      ],
      numberOfTime: 0,
      question: 'How many do you think would have occurred in the last month?',
      calculation: 'Enter criticality * number',
      criticality: 0,
      counter: 0,
      totalScore: -1,
    };
  }
}

export interface RiskQuestions {
  title: string;
  options: {
    label: string;
    selected: number;
    value: number;
  }[];
  question: string;
  numberOfTime: number;
  calculation?: string;
  isAnswered?: boolean;
  criticality: number;
  counter: number;
  totalScore: number;
}

const incidentOptions: string[] = [
  'Abandoned Object',
  'ANPR Unverified',
  'ANPR Verified',
  'Claims',
  'Communication Failure',
  'Device Faulty',
  'Device Offline',
  'Electricity Incident',
  'Face Unverified',
  'Face Verified',
  'Fire',
  'General Activity',
  'Housekeeping',
  'Incident',
  'Infrastructure Damage',
  'Intrusion',
  'Link Activity',
  'Loss/Damage of Property',
  'Over Utilised Energy',
  'Plumbing Incident',
  'Possible Crime',
  'Riot/Protest',
  'Security Incident',
  'Settled Claims',
  'Suspicious Behaviour',
  'Unattended Incident',
  'Unattended SoS',
  'UncheckedOutVisitor',
  'Unidentified Person',
  'Unmonitored Asset',
  'Unmonitored logs/activity',
  'Unsettled Claims',
  'Unverified Vehicle',
  'Unverified Visitor',
  'Unauthorised/Intruded Entry',
  'Exceeded Visitor Capacity',
  'Theft/Pilferage',
  'Under Utilised Energy',
  'Water Damage',
  'Structural Failure',
  'Roof Damage',
  'Glass Breakage',
  'HVAC Failure',
  'Earthquake',
  'Flood (Natural)',
  'Storm/Wind/Hail Damage',
  'Lightning Strike',
  'Wildfire',
  'Tornado/Hurricane',
  'Elevator/Escalator Malfunction',
  'Boiler or Pressure Vessel Explosion',
  'Generator or Backup Power Failure',
  'IT/Server Room Overheating or Damage',
  'Power Outage',
  'Gas Leak',
  'Water/Sewer Line Break',
  'Telecom/Internet Disruption',
  'Slip, Trip, and Fall',
  'Workplace Injury (Employee – Physical)',
  'Workplace Injury (Employee – Psychological)',
  'Visitor Injury',
  'Exposure to Hazardous Materials',
  'Fire Code Violations',
  'Contractor/3rd-party Incident',
  'Vandalism',
  'Cybersecurity Breach',
  'Trespassing/Break-in',
  'OSHA Violations',
  'Environmental Hazard',
  'Indoor Air Quality Issue',
  'Noise Pollution',
  'Hazardous Waste Spill or Leak',
  'Building Code Violations',
  'Permit/License Lapse',
  'Political Unrest/Riots Affecting Facility',
  'Regulatory Inspection Failure',
  'Health Code Violation',
  'Pandemic-related Disruption or Exposure',
  'Preventive Maintenance Missed',
  'Reactive Maintenance',
  'Inspection Failure',
  'Equipment Downtime',
  'Cleaning/Sanitation Deficiency',
  'Janitorial or Waste Management Lapse',
  'Delayed Repair Due to Vendor',
  'Defective Replacement Part',
  'Subcontractor Default',
  'Supply Chain Disruption',
  'Open Litigation/Dispute',
  'Fraudulent or Exaggerated Claim',
  'Insurance Lapse or Policy Gaps',
  'Denied Claim',
  'Subrogation Case',
  'IT – Data Loss or Corruption',
  'IT - Data Breach',
  'IT – Software Failure or Malfunction',
  'IT – Software Bug or Defect',
  'IT – Hardware Failure or Breakdown',
  'Untrained Staff Operating Equipment',
  'Emergency Exit Blocked or Malfunctioning',
  'Active Shooter or Armed Intruder Incident',
  'Bomb Threat or Suspicious Package',
  'Alarm System Malfunction',
  'Surveillance System Failure',
  'Fire Suppression System Failure',
  'Utility Misuse or Tampering',
  'Parking Lot Accident or Safety Hazard',
  'Ergonomic Injury or Musculoskeletal Claim',
  'Lockout/Tagout Procedure Violation',
];
const RISK_QUESTIONS: RiskQuestions[] = incidentOptions.map((option: string) =>
  createRiskQuestion(option)
);

function createRiskQuestion(title: string): RiskQuestions {
  return {
    title,
    options: [
      { label: 'High', selected: 0, value: 100 },
      { label: 'Medium', selected: 0, value: 50 },
      { label: 'Low', selected: 0, value: 25 },
      { label: 'No Risk', selected: 0, value: 0 },
    ],
    numberOfTime: 0,
    question: 'How many do you think would have occurred in the last month?',
    calculation: 'Enter criticality * number',
    criticality: 0,
    counter: 0,
    totalScore: -1,
  };
}
